import React, { useState, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import { useTranslation } from "react-i18next";
import {
  CircularProgress,
  FormHelperText,
  Typography,
  IconButton,
  Button
} from "@mui/material";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import OutlinedInput from "@mui/material/OutlinedInput";
import InputAdornment from "@mui/material/InputAdornment";
import ClearIcon from "@mui/icons-material/Clear";
import CheckIcon from "@mui/icons-material/Check";
import { green, red } from '@mui/material/colors';
import { useViewport } from '../../helpers';
import clsx from 'clsx';
import { categoryService } from '../../services';
import { useSnackbar } from 'notistack';

const PREFIX = 'CreateCategoryForm';

const classes = {
  rootContainer: `${PREFIX}-rootContainer`,
  greenIcon: `${PREFIX}-greenIcon`,
  redIcon: `${PREFIX}-redIcon`,
  marginBottom: `${PREFIX}-marginBottom`,
  marginTop: `${PREFIX}-marginTop`,
  doubleMarginTop: `${PREFIX}-doubleMarginTop`,
  marginRight: `${PREFIX}-marginRight`,
  iconPreviewContainer: `${PREFIX}-iconPreviewContainer`,
  imagePreviewContainer: `${PREFIX}-imagePreviewContainer`,
  submitButton: `${PREFIX}-submitButton`,
};

const Root = styled('div')((
  {
    theme
  }
) => ({
  [`&.${classes.rootContainer}`]: {
    minWidth: '300px',
  },

  [`& .${classes.greenIcon}`]: {
    color: green[500],
  },

  [`& .${classes.redIcon}`]: {
    color: red[500],
  },

  [`& .${classes.marginBottom}`]: {
    marginBottom: '1rem',
  },

  [`& .${classes.marginTop}`]: {
    marginTop: '1rem',
  },

  [`& .${classes.doubleMarginTop}`]: {
    marginTop: '2rem',
  },

  [`& .${classes.marginRight}`]: {
    marginRight: '1rem',
  },

  [`& .${classes.iconPreviewContainer}`]: {
    width: '90px',
  },

  [`& .${classes.imagePreviewContainer}`]: {
    width: '300px'
  },

  [`& .${classes.submitButton}`]: {
    marginTop: '2rem',
    marginBottom: '1rem',
    position: 'relative',
    left: '50%',
    transform: 'translateX(-50%)',
  },
}));

function useDebounce(value, delay) {
  // State and setters for debounced value
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(
    () => {
      // Update debounced value after delay
      const handler = setTimeout(() => {
        setDebouncedValue(value);
      }, delay);

      // Cancel the timeout if value changes (also on delay change or unmount)
      // This is how we prevent debounced value from updating if value is changed ...
      // .. within the delay period. Timeout gets cleared and restarted.
      return () => {
        clearTimeout(handler);
      };
    },
    [value, delay] // Only re-call effect if value or delay changes
  );

  return debouncedValue;
}

const CreateCategoryForm = (props) => {

  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [isNameUsed, setIsNameUsed] = useState(null);
  const [isNameSearching, setIsNameSearching] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [iconUrl, setIconUrl] = useState(null);
  const [icon, setIcon] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const debouncedSearchCategoryName = useDebounce(newCategoryName, 300);
  const { width } = useViewport();

  useEffect(() => {
    if (props.triggerUpdate) {
      let tmp = {
        ...props.selectedCategory,
        name: newCategoryName.length === 0 ? props.selectedCategory.name : newCategoryName,
        logo: icon ? icon : props.selectedCategory.logo,
        image: imageFile ? imageFile : props.selectedCategory.image,
      }
      props.setUpdateCategoryInfos(tmp);
    }
  }, [props.triggerUpdate])

  useEffect(() => {
    if (props.updateMod) {
      setNewCategoryName(props.selectedCategory.name);
      setIconUrl(props.selectedCategory.logo);
      setImageUrl(props.selectedCategory.image);
    }
  }, [])

  useEffect(
    () => {
      if ((!props.updateMod && debouncedSearchCategoryName && debouncedSearchCategoryName.length > 0) ||
        (props.updateMod && debouncedSearchCategoryName !== '' && debouncedSearchCategoryName.length > 0 && debouncedSearchCategoryName !== props.selectedCategory.name)) {
        setIsNameSearching(true);
        categoryService.categoryNameExist(debouncedSearchCategoryName)
          .then(res => {
            setIsNameUsed(res.exists);
            setIsNameSearching(false);
          })
      } else if (props.updateMod && debouncedSearchCategoryName === props.selectedCategory.name) {
        setIsNameUsed(false);
      }
    },
    [debouncedSearchCategoryName]
  );

  function handleImageReader(event, key) {
    let tmp = URL.createObjectURL(event.target.files[0])
    if (key === 'image') {
      setImageUrl(tmp);
      setImageFile(event.target.files);
    }
    else {
      setIconUrl(tmp);
      setIcon(event.target.files);
    }
  }

  const handleSubmit = () => {
    setLoading(true)
    categoryService.createCategory(
      newCategoryName,
      icon,
      imageFile,
      () => enqueueSnackbar(t('toasts.createCategoryError'), { variant: 'error', }),
      () => enqueueSnackbar(t('toasts.createCategorySuccess'), { variant: 'success', }))
      .then(res => {
        setLoading(false);
        if (!props.updateMod)
          props.closeModal();
        props.refreshCategories();
      })
  }

  return (
    <Root>
      <div className={classes.rootContainer}>
        <div className={props.updateMod ? '' : classes.doubleMarginTop}>
          <Typography variant="h5" className={clsx(classes.marginBottom, classes.marginTop)}>{t("categories.create.subtitle")}</Typography>
          <FormControl variant="outlined">
            <InputLabel htmlFor="name">{t("categories.create.categoryName")}</InputLabel>
            <OutlinedInput
              name="addTargetName"
              label={t("categories.create.categoryName")}
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              className={width > 1024 ? classes.marginRight : ''}
              variant={"outlined"}
              endAdornment={(isNameUsed !== null || isNameSearching) &&
                <InputAdornment position="end">
                  <IconButton aria-label="check if category name is available" size="large">
                    {isNameSearching ? <CircularProgress /> : isNameUsed ? <ClearIcon className={classes.redIcon} /> :
                      <CheckIcon className={classes.greenIcon} />}
                  </IconButton>
                </InputAdornment>
              }
            />
          </FormControl>
          {isNameUsed &&
            <FormHelperText id="targetNameUsed" error className={clsx(classes.marginBottom)}>
              {t('categories.create.nameAlreadyUsed')}
            </FormHelperText>
          }
        </div>
        <div className={classes.doubleMarginTop}>
          <Typography variant="h5" className={clsx(classes.marginBottom, classes.marginTop)}>{t("categories.create.iconTitle")}</Typography>
          {iconUrl &&
            <img
              id="icon"
              className={classes.iconPreviewContainer}
              alt={iconUrl ? 'icon' : null}
              src={iconUrl}
            />
          }
          <div className={classes.marginTop}>
            <Button
              variant="contained"
              color="primary"
              component="label"
              onChange={(e) => handleImageReader(e, 'icon')}
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              {t('categories.create.selectFile')}
              <input
                type="file"
                accept="image/*"
                name="icon"
                hidden
              />
            </Button>
            <Typography variant="body1">
              {icon && icon.length > 0 ? icon[0].name : ''}
            </Typography>
          </div>
        </div>
        <div className={classes.doubleMarginTop}>
          <Typography variant="h5" className={clsx(classes.marginBottom, classes.marginTop)}>{t("categories.create.imageTitle")}</Typography>
          {imageUrl &&
            <img
              id="image"
              className={classes.imagePreviewContainer}
              alt={imageUrl ? 'image' : null}
              src={imageUrl}
            />
          }
          <div className={classes.marginTop}>
            <Button
              variant="contained"
              color="primary"
              component="label"
              onChange={(e) => handleImageReader(e, 'image')}
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              {t('categories.create.selectFile')}
              <input
                type="file"
                accept="image/*"
                name="image"
                hidden
              />
            </Button>
            <Typography variant="body1">
              {imageFile && imageFile.length > 0 ? imageFile[0].name : ''}
            </Typography>
          </div>
        </div>
        {!props.updateMod &&
          <Button
            color="primary"
            variant="contained"
            onClick={() => handleSubmit()}
            className={clsx(classes.submitButton)}
            disabled={!icon || !imageFile || isNameUsed || newCategoryName.length === 0 || loading}
          >
            {loading ? <CircularProgress size={28} /> : t("categories.create.create")}
          </Button>
        }
      </div>
    </Root>
  );
}

export default CreateCategoryForm;